import { Localized, useLocalization } from "@fluent/react"
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail"
import BusinessIcon from "@mui/icons-material/Business"
import CategoryIcon from "@mui/icons-material/Category"
import CloseIcon from "@mui/icons-material/Close"
import LocationCityIcon from "@mui/icons-material/LocationCity"
import MapIcon from "@mui/icons-material/Map"
import PersonIcon from "@mui/icons-material/Person"
import PhoneIcon from "@mui/icons-material/Phone"
import PublicIcon from "@mui/icons-material/Public"
import ShortTextIcon from "@mui/icons-material/ShortText"
import { DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material"
import { useCallback, useEffect, useMemo, useState } from "react"
import { CancelButton } from "../../../app/components/CancelButton"
import { EnumMultiSelect } from "../../../app/components/EnumMultiSelect"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { validateEmail } from "../../../app/Utils"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { QUICK_TAG_OPTIONS } from "../../administration/companyProfile/enums/QuickTagEnum"
import { selectUserCompanyId } from "../../user/userSlice"
import { createSupplier, DraftSupplier, selectSupplierById, updateSupplier, Supplier, SupplierTypeEnum, SUPPLIER_TYPE_OPTIONS } from "../suppliersSlice"

type CreateSupplierDialogProps = {
    onSuccess: (supplier: Supplier) => void
    onCancel: () => void
    mode?: "create" | "edit"
    supplierId?: string
    structureId?: string
}

type SupplierDraftErrors = {
    name: boolean
    shortName: boolean
    email: boolean
    supplierType: boolean
}

type QuickTagOption = {
    id: number
    name: string
}

const initialState: DraftSupplier = {
    name: "",
    shortName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
    contactPerson: "",
    email: "",
    phone: "",
    mobile: "",
    supplierType: "",
    quickTags: [],
}

const Section = ({
    title,
    children,
}: {
    title: React.ReactNode
    children: React.ReactNode
}) => (
    <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, overflowX: "hidden" }}
    >
        <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
            {children}
        </Stack>
    </Paper>
)

export const CreateSupplierDialog = ({
    onSuccess,
    onCancel,
    mode = "create",
    supplierId,
    structureId
}: CreateSupplierDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const userCompanyId = useAppSelector(selectUserCompanyId)
    const entity = useAppSelector(state => selectSupplierById(state, supplierId ?? ""));
    const [errors, setErrors] = useState<SupplierDraftErrors>({
        name: false,
        shortName: false,
        email: false,
        supplierType: false,
    })
    const [saving, setSaving] = useState(false)
    
    const quickTagOptions = useMemo<QuickTagOption[]>(
        () => QUICK_TAG_OPTIONS.slice(0, 4).map((option) => ({ id: option.id, name: option.label })),
        []
    )

    const quickTagIdSet = useMemo(
        () => new Set(quickTagOptions.map((option) => option.id)),
        [quickTagOptions]
    )

    const normalizeQuickTags = useCallback((quickTags?: Array<string | number>) => {
        if (!quickTags) {
            return []
        }

        return Array.from(new Set(quickTags
            .map((tag) => {
                const numericTag = Number(tag)
                if (!Number.isFinite(numericTag) || !quickTagIdSet.has(numericTag)) {
                    return null
                }

                return numericTag
            })
            .filter((tag): tag is number => typeof tag === "number")))
    }, [quickTagIdSet])

    const draftFromEntity = useMemo<DraftSupplier>(() => (mode === "edit" && entity ? {
        ...initialState,
        name: entity.name ?? "",
        shortName: entity.shortName ?? "",
        address1: entity.address1 ?? "",
        address2: entity.address2 ?? "",
        city: entity.city ?? "",
        state: entity.state ?? "",
        zipcode: entity.zipcode ?? "",
        country: entity.country ?? "",
        contactPerson: entity.contactPerson ?? "",
        email: entity.email ?? "",
        phone: entity.phone ?? "",
        mobile: entity.mobile ?? "",
        supplierType: entity.supplierType && (entity.supplierType as unknown) !== 0 ? entity.supplierType : "",
        quickTags: normalizeQuickTags((entity.quickTags ?? []).map((tag) => tag.quickTagId)),
    } : { ...initialState }), [entity, mode, normalizeQuickTags])

    const [draft, setDraft] = useState<DraftSupplier>(draftFromEntity)
    
    useEffect(() => {
        setDraft(draftFromEntity)
        setErrors({
            name: false,
            shortName: false,
            email: false,
            supplierType: false,
        })
        setSaving(false)
    }, [draftFromEntity])

    const handleTextChange =
        (field: Exclude<keyof DraftSupplier, "supplierType" | "quickTags">) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            setDraft((prev) => ({ ...prev, [field]: value }))
        }

    const validate = () => {
        const nextErrors: SupplierDraftErrors = {
            name: draft.name.trim() === "",
            shortName: draft.shortName.trim() === "",
            email: draft.email.trim() === "" || !validateEmail(draft.email),
            supplierType: draft.supplierType === "",
        }

        setErrors(nextErrors)
        return !Object.values(nextErrors).some(Boolean)
    }

    const handleSave = async () => {
        if (!validate() || !userCompanyId) {
            return
        }

        const payload: DraftSupplier = {
            name: draft.name,
            shortName: draft.shortName,
            address1: draft.address1,
            address2: draft.address2,
            city: draft.city,
            state: draft.state,
            zipcode: draft.zipcode,
            country: draft.country,
            contactPerson: draft.contactPerson,
            email: draft.email,
            phone: draft.phone,
            mobile: draft.mobile,
            supplierType: draft.supplierType as SupplierTypeEnum,
            quickTags: Array.from(new Set(draft.quickTags)),
        }

        setSaving(true)
        try {
            if (mode === "edit") {
                if (!entity) return

                const result = await dispatch(updateSupplier({ ...entity, ...payload }))
                if (updateSupplier.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"));
                    onSuccess(result.payload);
                } else {
                    dispatch(showError("error"));
                }
            } else {
                const result = await dispatch(createSupplier({ structureId, ...payload }))
                if (createSupplier.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    onSuccess(result.payload);
                } else {
                    dispatch(showError("error"));
                }
            }

        } catch (error) {
            dispatch(showError("error"))
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
                {mode === "edit" ? (
                    <Localized id="edit">_Edytuj</Localized>
                ) : (
                    <Localized id="supplier-create-dialog-title">_Utwórz dostawcę</Localized>
                )}
                <IconButton
                    onClick={onCancel}
                    disabled={saving}
                    size="small"
                    sx={{ position: "absolute", right: 12, top: 12 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 3, px: 3 }}>
                <Stack spacing={2.5}>
                    <Section title={l10n.getString("company", null, "_Firma")}>
                        <Grid container spacing={2} sx={{ m: 0, width: "100%" }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    value={draft.name}
                                    onChange={handleTextChange("name")}
                                    label={l10n.getString("supplier-name", null, "_Nazwa")}
                                    error={errors.name}
                                    helperText={errors.name ? l10n.getString("field-is-required", null, "_Pole jest wymagane") : undefined}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <BusinessIcon fontSize="small" sx={{ color: "primary.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    value={draft.shortName}
                                    onChange={handleTextChange("shortName")}
                                    label={l10n.getString("supplier-shortName", null, "_Krótka nazwa")}
                                    error={errors.shortName}
                                    helperText={errors.shortName ? l10n.getString("field-is-required", null, "_Pole jest wymagane") : undefined}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ShortTextIcon fontSize="small" sx={{ color: "secondary.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    value={draft.supplierType}
                                    onChange={(event) => {
                                        const value = event.target.value
                                        setDraft((prev) => ({
                                            ...prev,
                                            supplierType: value as SupplierTypeEnum | "",
                                        }))
                                    }}
                                    label={l10n.getString("supplier-type", null, "_Typ dostawcy")}
                                    error={errors.supplierType}
                                    helperText={errors.supplierType ? l10n.getString("field-is-required", null, "_Pole jest wymagane") : undefined}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CategoryIcon fontSize="small" sx={{ color: "info.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                >
                                    {SUPPLIER_TYPE_OPTIONS.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {l10n.getString(`supplier-type-${option.id}`, null, `_${option.label}`)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <EnumMultiSelect
                                    label={l10n.getString("supplier-tags", null, "_Tagi")}
                                    placeholder={l10n.getString("supplier-tags-placeholder", null, "_Wybierz sugerowane tagi")}
                                    value={draft.quickTags.length > 0 ? draft.quickTags : null}
                                    options={quickTagOptions}
                                    onChange={(ids) =>
                                        setDraft((prev) => {
                                            const numeric = ids ? ids.map((id) => Number(id)).filter((id) => Number.isFinite(id)) : []
                                            return {
                                                ...prev,
                                                quickTags: Array.from(new Set(numeric.filter((id) => quickTagIdSet.has(id)))),
                                            }
                                        })
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Section>

                    <Section title={l10n.getString("supplier-address-data", null, "_Adres")}>
                        <Grid container spacing={2} sx={{ m: 0, width: "100%" }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={draft.address1}
                                    onChange={handleTextChange("address1")}
                                    label={l10n.getString("supplier-address-first", null, "_Adres 1")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <MapIcon fontSize="small" sx={{ color: "warning.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={draft.address2}
                                    onChange={handleTextChange("address2")}
                                    label={l10n.getString("supplier-address-second", null, "_Adres 2")}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={draft.city}
                                    onChange={handleTextChange("city")}
                                    label={l10n.getString("supplier-address-city", null, "_Miasto")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocationCityIcon fontSize="small" sx={{ color: "primary.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={draft.state}
                                    onChange={handleTextChange("state")}
                                    label={l10n.getString("supplier-address-state", null, "_Województwo / Region")}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={draft.zipcode}
                                    onChange={handleTextChange("zipcode")}
                                    label={l10n.getString("supplier-address-zip-code", null, "_Kod pocztowy")}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={draft.country}
                                    onChange={handleTextChange("country")}
                                    label={l10n.getString("supplier-address-country", null, "_Kraj")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PublicIcon fontSize="small" sx={{ color: "success.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Section>

                    <Section title={l10n.getString("supplier-contact-data", null, "_Dane kontaktowe")}>
                        <Grid container spacing={2} sx={{ m: 0, width: "100%" }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={draft.contactPerson}
                                    onChange={handleTextChange("contactPerson")}
                                    label={l10n.getString("supplier-contact-person", null, "_Osoba kontaktowa")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon fontSize="small" sx={{ color: "secondary.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    value={draft.email}
                                    onChange={handleTextChange("email")}
                                    label={l10n.getString("supplier-email", null, "_Email")}
                                    error={errors.email}
                                    helperText={errors.email ? l10n.getString("email-incorrect", null, "_Niepoprawny email") : undefined}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AlternateEmailIcon fontSize="small" sx={{ color: "primary.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={draft.phone}
                                    onChange={handleTextChange("phone")}
                                    label={l10n.getString("supplier-phone", null, "_Tel")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneIcon fontSize="small" sx={{ color: "warning.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={draft.mobile}
                                    onChange={handleTextChange("mobile")}
                                    label={l10n.getString("supplier-mobile", null, "_Kom")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneIcon fontSize="small" sx={{ color: "success.main" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Section>

                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <SaveButton loading={saving} onClick={handleSave} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </>
    )
}